import React, { useEffect, useState } from "react"
import Button from "aldoo-ra/Button"
import TextField from "aldoo-ra/TextField"
import DropdownSelect from "aldoo-ra/DropdownSelect"
import Modal from "aldoo-ra/Modal"
import { showPromptDialogAsync } from "aldoo-ra/PromptDialog"
import ToggleSwitch from "aldoo-ra/ToggleSwitch"
import { TrashIcon, PencilSquareIcon, EnvelopeIcon, ChatBubbleLeftRightIcon, DevicePhoneMobileIcon, PhoneIcon, ChevronDownIcon, ChevronRightIcon } from "@heroicons/react/24/outline"

const KIND_ICON = {
  email: EnvelopeIcon,
  slack: ChatBubbleLeftRightIcon,
  sms: DevicePhoneMobileIcon,
  voice: PhoneIcon,
}
import { listNotificationChannels, upsertNotificationChannel, deleteNotificationChannel } from "../api"

const KIND_OPTIONS = [
  { value: "slack", label: "Slack" },
  { value: "email", label: "Email" },
  { value: "sms", label: "SMS" },
  { value: "voice", label: "Voice" },
]

const blankConfigByKind = {
  slack: { webhookUrl: "" },
  email: { address: "" },
  sms: { phone: "" },
  voice: { phone: "" },
}

const ChannelTargetsManager = ({ orgId }) => {
  const [channels, setChannels] = useState([])
  const [editing, setEditing] = useState(null)

  const refresh = async () => {
    const r = await listNotificationChannels(orgId)
    setChannels(r?.channels || [])
  }
  useEffect(() => { refresh() }, [orgId])

  const onDelete = async (c) => {
    const ok = await showPromptDialogAsync({
      title: "Delete channel",
      message: `Delete channel "${c.name}"? Rules referencing it will lose this target.`,
      confirmText: "Delete",
      cancelText: "Cancel",
    })
    if (!ok) return
    await deleteNotificationChannel(c._id)
    refresh()
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-sm text-text_secondary">Register named notification targets (Slack channels, email addresses, etc.) that rules can subscribe to.</div>
        <Button onClick={() => setEditing({})}>+ Channel</Button>
      </div>

      <div className="grid gap-2">
        {channels.length === 0 && <div className="text-text_secondary text-sm">No channels yet.</div>}
        {channels.map((c) => (
          <div key={c._id} className="p-3 rounded border border-bordered bg-paper flex items-center justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                {(() => {
                  const Icon = KIND_ICON[c.kind]
                  return Icon ? <Icon className="w-4 h-4 text-primary shrink-0" title={c.kind} /> : <span className="text-xs px-2 py-0.5 rounded bg-primary/10 text-primary">{c.kind}</span>
                })()}
                <span className="font-medium truncate">{c.name}</span>
                {c.kind === "email" && c.config?.address && (
                  <span className="text-xs text-text_secondary truncate">{c.config.address}</span>
                )}
                {(c.kind === "sms" || c.kind === "voice") && c.config?.phone && (
                  <span className="text-xs text-text_secondary truncate">{c.config.phone}</span>
                )}
                {c.kind === "slack" && c.config?.webhookUrl && (
                  <span className="text-xs text-text_secondary truncate" title={c.config.webhookUrl}>
                    {c.config.webhookUrl.replace(/^https:\/\/hooks\.slack\.com\/services\//, "")}
                  </span>
                )}
                {!c.enabled && <span className="text-xs text-text_secondary">disabled</span>}
              </div>
            </div>
            <div className="flex gap-2 shrink-0">
              <button onClick={() => setEditing(c)} className="p-2 rounded hover:bg-primary/10 text-primary"><PencilSquareIcon className="w-5 h-5" /></button>
              <button onClick={() => onDelete(c)} className="p-2 rounded hover:bg-error/10 text-error"><TrashIcon className="w-5 h-5" /></button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <ChannelEditor
          orgId={orgId}
          channel={editing._id ? editing : null}
          onClose={() => { setEditing(null); refresh() }}
        />
      )}
    </div>
  )
}

const ChannelEditor = ({ orgId, channel, onClose }) => {
  const isEdit = !!channel?._id
  const [kind, setKind] = useState(KIND_OPTIONS.find((o) => o.value === (channel?.kind || "slack")))
  const [name, setName] = useState(channel?.name || "")
  const [enabled, setEnabled] = useState(channel?.enabled !== false)
  const [config, setConfig] = useState(channel?.config || blankConfigByKind[channel?.kind || "slack"])
  const [err, setErr] = useState(null)
  const [showSlackHelp, setShowSlackHelp] = useState(false)

  useEffect(() => {
    if (!isEdit) setConfig(blankConfigByKind[kind?.value] || {})
  }, [kind?.value])

  const onSave = async () => {
    setErr(null)
    const r = await upsertNotificationChannel({
      channelId: isEdit ? channel._id : undefined,
      orgId,
      kind: kind?.value,
      name,
      config,
      enabled,
    })
    if (r?.error) return setErr(r.error)
    onClose()
  }

  const configField = (key, label, placeholder) => (
    <TextField label={label} value={config[key] || ""} onChange={(v) => setConfig({ ...config, [key]: v })} placeholderText={placeholder} />
  )

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={isEdit ? "Edit channel" : "New channel"}
      variant="compact"
      className="max-w-md"
      closeOnClickOutside
      closeOnEscape
    >
      <div className="p-4 space-y-3">
        <div className="flex justify-end items-center gap-2 -mt-2">
          <span className="text-sm font-medium text-text_secondary">Enabled</span>
          <ToggleSwitch isOn={enabled} onChange={setEnabled} />
        </div>

        <div>
          <label className="block mb-2 text-sm font-medium text-text_secondary">Kind</label>
          <DropdownSelect
            value={kind}
            onChange={(v) => setKind(v?.[0] || v)}
            options={KIND_OPTIONS}
            showCheckbox={false}
            renderOption={(opt) => {
              const Icon = KIND_ICON[opt.value]
              return (
                <div className="flex items-center gap-2 py-1">
                  {Icon && <Icon className="w-4 h-4 text-primary shrink-0" />}
                  <span className="text-sm">{opt.label}</span>
                </div>
              )
            }}
            onValueItemRender={(opt) => {
              if (!opt) return <span className="text-text_secondary text-sm">Pick a kind</span>
              const Icon = KIND_ICON[opt.value]
              return (
                <div className="flex items-center gap-2">
                  {Icon && <Icon className="w-4 h-4 text-primary shrink-0" />}
                  <span className="text-sm">{opt.label}</span>
                </div>
              )
            }}
          />
        </div>

        <TextField label="Name" value={name} onChange={setName} placeholderText={kind?.value === "slack" ? "#incidents" : "Ops team"} />

        {kind?.value === "slack" && (
          <>
            {configField("webhookUrl", "Slack webhook URL", "https://hooks.slack.com/services/...")}
            <button
              type="button"
              onClick={() => setShowSlackHelp((v) => !v)}
              className="flex items-center gap-1 text-xs font-medium text-link hover:underline w-fit"
            >
              {showSlackHelp ? <ChevronDownIcon className="w-4 h-4" /> : <ChevronRightIcon className="w-4 h-4" />}
              How do I get a Slack webhook URL?
            </button>
            {showSlackHelp && (
              <div className="text-xs text-text_secondary bg-canvas border border-bordered rounded p-3 space-y-1 leading-relaxed">
                <div>1. Open <a href="https://api.slack.com/apps" target="_blank" rel="noreferrer" className="text-link hover:underline">api.slack.com/apps</a> and click <b>Create New App</b> → <b>From scratch</b>.</div>
                <div>2. Name it (e.g. "Aldoo Status") and pick the workspace.</div>
                <div>3. In the app sidebar, open <b>Incoming Webhooks</b> and toggle it <b>On</b>.</div>
                <div>4. Click <b>Add New Webhook to Workspace</b>, pick the channel that should receive alerts (e.g. <code>#incidents</code>), and authorize.</div>
                <div>5. Copy the generated URL (<code>https://hooks.slack.com/services/T.../B.../...</code>) and paste it above.</div>
                <div className="text-text_secondary/80 pt-1">The URL is a secret — anyone with it can post to that channel.</div>
              </div>
            )}
          </>
        )}
        {kind?.value === "email" && configField("address", "Email address", "ops@example.com")}
        {(kind?.value === "sms" || kind?.value === "voice") && configField("phone", "Phone number (E.164)", "+359...")}

        <div className="flex flex-col items-center gap-2 pt-2">
          <Button onClick={onSave} className="px-10 py-3 text-base">{isEdit ? "Save" : "Create"}</Button>
          {err && <div className="text-error text-sm">Error: {err}</div>}
        </div>
      </div>
    </Modal>
  )
}

export default ChannelTargetsManager
